import {
	Component,
	EventEmitter,
	Input,
	OnDestroy,
	OnInit,
	Output,
	ViewChild
} from "@angular/core";
import { BehaviorSubject, combineLatest, EMPTY, Observable, of, ReplaySubject, Subject, Subscription } from "rxjs";
import { FormGroup } from "@angular/forms";
import { PersonaFisicaDTO } from "../../models/persona-fisica-dto";
import { ComponentOutputStatus } from "../../normalizzazione/component-output-status";
import { InfoAnagrafichePFDTO } from "../../models/info-anagrafiche-pf";
import { InfoNascitaPFDTO } from "../../models/info-nascita-pf.dto";
import { Localita } from "../../models/portale/localita";
import { Nazione } from "../../models/portale/nazione";
import { DelegheService } from '../../services/deleghe-services/deleghe.service';
import { DatiVerificaCfDTO } from "../../models/dati-verifca-dto";
import { isNotNull, isNotUndefined, not } from "../../utils/fnutils";
import { auditTime, catchError, concatMap, filter, map, pluck, sampleTime, startWith, switchMap, tap, withLatestFrom } from "rxjs/operators";
import * as moment from "moment";
import { ProvinciaEstesaDTO } from "../../models/provinciaEstesaDTO";
import { InfoPfComponent } from "../info-pf/info-pf.component";
import {InfoNascitaComponent} from "../info-nascita/info-nascita.component"

const SAMPLE_TIME = 1000;

const EMPTY_PF: PersonaFisicaDTO = {
	id: null,
	infoAnagrafiche: {
		codFiscale: null,
		codiceRegione: null,
		cognome: null,
		nome: null
	},
	infoNascita: {
		comuneNascita: null,
		dataNascita: null,
		provinciaNascita: null,
		nazioneNascita: null,
		isNatoEstero: null,
		codCatNazioneNascita: null
	}
};
@Component({
	selector: "lib-persona-fisica",
	templateUrl: "./persona-fisica.component.html",
	styleUrls: ["./persona-fisica.component.css"],
})
export class PersonaFisicaComponent implements OnInit, OnDestroy {

	form: FormGroup;

	@Input()
	infoPersonaFisica: PersonaFisicaDTO;

	@Input()
	cfDifferentFrom: string;

	@Input()
	province: Observable<Array<Localita>>;

	@Input()
	nazioni: Observable<Array<Nazione>>;

	@Input()
	namespace: string;

	@Input()
	idDelega: string;
	@Input()
	nuovaPersona: boolean;
	@Output()
	done: EventEmitter<
		ComponentOutputStatus<PersonaFisicaDTO>
	> = new EventEmitter<ComponentOutputStatus<PersonaFisicaDTO>>();

	changesPF$ = new ReplaySubject<ComponentOutputStatus<InfoAnagrafichePFDTO>>(1);

	changesNascita$ = new ReplaySubject<ComponentOutputStatus<InfoNascitaPFDTO>>(1);

	namespaceInfoPf: string;
	namespaceInfoNascita: string;

	personaFisicaChangedSub: Subscription;

	readOnly: boolean = false;

	isPersonaFisicaValid: BehaviorSubject<boolean> = new BehaviorSubject(false);

	datiVerifica = {} as DatiVerificaCfDTO;

	@ViewChild('infoPF')
	infoPF: InfoPfComponent;

	@ViewChild('infoNascita')
	infoNascita: InfoNascitaComponent;

	constructor(private delegheService: DelegheService) { }

	ngOnInit() {

		this.form = new FormGroup({});
		let valid: boolean = false;
		if (!this.infoPersonaFisica) {
			this.initPersonaFisica();
			valid = false;
		} else {
			this.readOnly = true;
		}

		if (this.idDelega == null || this.nuovaPersona) {
			this.readOnly = false;
		}

		this.namespaceInfoPf = this.namespace + "infoPF";
		this.namespaceInfoNascita = this.namespace + "infoNascita";

		const mappaProvince$ = this.delegheService.getAllProvince();

		this.personaFisicaChangedSub = combineLatest(
			[this.changesPF$, this.changesNascita$, mappaProvince$]
		)
		.pipe(
			map(([validPF, changesNascita, allProvince]) => {
				let provincia = allProvince[changesNascita?.output?.provinciaNascita];
				return [validPF, changesNascita, provincia]
			}),
			auditTime(SAMPLE_TIME),
			concatMap(([infoAnagrafiche, infoNascita, provincia]) =>
				this.getPersonaFisicaValid(infoAnagrafiche, infoNascita, provincia)
			),
			startWith(ComponentOutputStatus.of(valid, this.infoPersonaFisica)),
			tap(pf => this.done.emit(pf))
		)
		.subscribe();
	}

	getPersonaFisicaValid(
		infoAnagrafiche: ComponentOutputStatus<InfoAnagrafichePFDTO>,
		infoNascita: ComponentOutputStatus<InfoNascitaPFDTO>,
		provincia?: ProvinciaEstesaDTO
	) {

		console.log("FORM VALIDITY", infoNascita.status);
		if (!infoNascita.status || !infoAnagrafiche.status) {
		
			this.isPersonaFisicaValid.next(false);
			return of(ComponentOutputStatus.of(false, EMPTY_PF));
		}

		return this.validatePersonaFisica(infoAnagrafiche.output, infoNascita.output)
			.pipe(
				
				tap(() => this.isPersonaFisicaValid.next(true)),
				map((response: { id: string } | null) => {
					const isPersonaFisicaValid = isNotNull(response);
					return this.toValidPf(isPersonaFisicaValid, response, infoAnagrafiche.output, provincia, infoNascita)
				}),
				tap(({ isValid }) => this.isPersonaFisicaValid.next(isValid)),
				tap(({ persona }) => this.infoPersonaFisica = persona),
				map(v => ComponentOutputStatus.of(v.isValid, v.persona)),
				catchError(_ => of(ComponentOutputStatus.of(false, EMPTY_PF)))
			);
	}

	private toValidPf(
		isPersonaFisicaValid: boolean, response: { id: string; }, infoAnagrafiche: InfoAnagrafichePFDTO, 
		provincia: ProvinciaEstesaDTO, 
		infoNascita: ComponentOutputStatus<InfoNascitaPFDTO>): { isValid: boolean; persona: PersonaFisicaDTO; } {
		
		return {
			isValid: isPersonaFisicaValid,
			persona: {
				id: response?.id,
				infoAnagrafiche: {
					codFiscale: String(infoAnagrafiche.codFiscale),
					codiceRegione: String(provincia?.siglaReg),
					cognome: String(infoAnagrafiche.cognome),
					nome: String(infoAnagrafiche.nome)
				},
				infoNascita: {
					comuneNascita: infoNascita?.output?.comuneNascita ? String(infoNascita.output.comuneNascita) : null,
					dataNascita: infoNascita.output.dataNascita,
					provinciaNascita: String(infoNascita.output.provinciaNascita),
					nazioneNascita: String(infoNascita.output.nazioneNascita),
					codCatNazioneNascita: String(infoNascita.output.codCatNazioneNascita),
					isNatoEstero: infoNascita.output?.isNatoEstero ? infoNascita.output.isNatoEstero : false
				}
			} as PersonaFisicaDTO
		};
	}

	validatePersonaFisica(
		infoAnagrafiche: InfoAnagrafichePFDTO,
		infoNascita: InfoNascitaPFDTO
	) {

		// var d = new Date(infoNascita.dataNascita);
		// d.toLocaleString('it-IT', { timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone })
		return this.delegheService.getValidaPersonaFisica(
			{
				codiceFiscale: infoAnagrafiche.codFiscale
				, cognome: infoAnagrafiche.cognome
				, comune: infoNascita?.isNatoEstero ? null : infoNascita?.comuneNascita
				// , dataNascita: new Date(moment(infoNascita.dataNascita, 'DD/MM/YYYY').add(23, 'hour').add(59, 'minute').add(59, 'second').toDate())
				, dataNascita: new Date(infoNascita.dataNascita).toISOString()
				, nome: infoAnagrafiche.nome
				, provinciaNascita: infoNascita.provinciaNascita
				, siglaNazione: infoNascita.nazioneNascita
				, codCatNazioneNascita: infoNascita?.codCatNazioneNascita
			} as DatiVerificaCfDTO
		);
	}

	initPersonaFisica() {
		this.infoPersonaFisica = new PersonaFisicaDTO();
	}

	infoPfReady(inFoPfStatus: ComponentOutputStatus<InfoAnagrafichePFDTO>) {
		// console.log("[infoPfReady]", inFoPfStatus);

		this.changesPF$.next(inFoPfStatus);
	}

	infoNascitaReady(infoNascitaPf: ComponentOutputStatus<InfoNascitaPFDTO>) {
		// console.log("[infoNascitaReady]", infoNascitaPf);

		this.changesNascita$.next(infoNascitaPf);
	}

	ngOnDestroy(): void {
		this.personaFisicaChangedSub.unsubscribe();
	}

	public resetForm() {
		this.infoPF._resetForm();
		this.infoNascita._resetForm();
	}


}
